const CHUNK_LOAD_RECOVERY_STORAGE_KEY = "__renda_chunk_load_recovery_at";
const CHUNK_LOAD_RECOVERY_COOLDOWN_MS = 10_000;
const CHUNK_LOAD_ERROR_MARKERS = [
  "Failed to fetch dynamically imported module",
  "Importing a module script failed",
  "ChunkLoadError",
  "Unable to preload CSS",
];

type ErrorLike = {
  message?: unknown;
};

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  if (error && typeof error === "object" && "message" in error) {
    const message = (error as ErrorLike).message;

    if (typeof message === "string") {
      return message;
    }
  }

  return undefined;
};

const getLastRecoveryAt = () => {
  try {
    return Number(sessionStorage.getItem(CHUNK_LOAD_RECOVERY_STORAGE_KEY) ?? 0);
  } catch {
    return 0;
  }
};

const setLastRecoveryAt = () => {
  try {
    sessionStorage.setItem(CHUNK_LOAD_RECOVERY_STORAGE_KEY, String(Date.now()));
  } catch {
    // noop
  }
};

export const clearExpiredChunkLoadRecoveryState = () => {
  if (typeof window === "undefined") {
    return;
  }

  const lastRecoveryAt = getLastRecoveryAt();

  if (
    lastRecoveryAt > 0 &&
    Date.now() - lastRecoveryAt >= CHUNK_LOAD_RECOVERY_COOLDOWN_MS
  ) {
    try {
      sessionStorage.removeItem(CHUNK_LOAD_RECOVERY_STORAGE_KEY);
    } catch {
      // noop
    }
  }
};

export const isChunkLoadError = (error: unknown) => {
  const message = getErrorMessage(error);

  return (
    typeof message === "string" &&
    CHUNK_LOAD_ERROR_MARKERS.some((marker) => message.includes(marker))
  );
};

export const reloadApplication = () => {
  if (typeof window !== "undefined") {
    window.location.reload();
  }
};

export const recoverFromChunkLoadError = () => {
  if (typeof window === "undefined") {
    return false;
  }

  const lastRecoveryAt = getLastRecoveryAt();

  if (
    lastRecoveryAt > 0 &&
    Date.now() - lastRecoveryAt < CHUNK_LOAD_RECOVERY_COOLDOWN_MS
  ) {
    return false;
  }

  setLastRecoveryAt();
  reloadApplication();

  return true;
};

export type VitePreloadErrorEvent = Event & {
  payload?: unknown;
};
